import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode, useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import BenchlingConfig from 'BenchlingConfig';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import runLookupConfigModalForLabAutomation from 'lab-automation/settings/runLookupConfigModalForLabAutomation';
import ProcessTasksLookupStepDropdown from 'processes/settings/ProcessTasksLookupStepDropdown';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {FONT_FAMILY_PROXIMA, FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import {miniSpacingBottom} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, BLUE_POWDER_LIGHT, GRAY, GRAY_DARKER, RED_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal, {ModalProps} from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';

import FilterLookupStepPopover from './FilterLookupStepPopover';
import {LookupInfosDescription} from './LookupConfigDescription';
import {
  ConstantLookupStepSpec,
  DEFAULT_FINAL_LOOKUP_STEP_TYPES,
  DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  FilterLookupStepInfo,
  FIRST_STEP_OPTIONS_FOR_COLUMNS_MAP,
  FormErrors,
  InjectedLookupStep,
  LookupConfigModalContextInfo,
  LookupConfigSpec,
  LookupSortType,
  LookupStepInfo,
  LookupStepSpec,
  LookupStepType,
  ReplicatesLookupStepSpec,
  TaskFinderProcessTasksLookupStepSpec,
} from './LookupConfigModalConstants';
import LookupConfigModalErrorIndicator from './LookupConfigModalErrorIndicator';
import {LookupConfigModalPaneIndicator} from './LookupConfigModalPaneIndicator';
import {
  getConstantReplicationFactor,
  getIconForLookupStepInfo,
  getLookupStepInfoWithType,
  getObjectTypesForLookupStepInfo,
  getSchemaFieldsForLookupStep,
  getStepDisplayInfo,
} from './LookupConfigUtils';
import LookupStepForm, {isLookupStepConfigurable} from './LookupStepForm';
import {
  getLookupStepInfoForCountStepItem,
  getLookupStepInfoForFieldStepItem,
  getLookupStepInfoForNonFieldStepItem,
} from './LookupStepInitializerUtils';
import ReplicateByConstantModal from './ReplicateByConstantModal';
import validateLookupSteps, {isFormErrorsEmpty} from './validateLookupSteps';

interface LookupDetailOption extends Omit<LookupStepInfo, 'lookupStep'> {
  icon?: BenchlingIconName;
  lookupStep: LookupStepSpec | null;
  isDeleted?: boolean;
}

function getStepTypes(
  previousStepInfo: LookupStepInfo,
  initialDetailStep: LookupStepInfo | null,
  allowedLookupStepTypes: Array<LookupStepType>
): Array<LookupStepType> {
  const lookupStepInfoWithType = getLookupStepInfoWithType(previousStepInfo);
  const parentSchema = lookupStepInfoWithType?.linkSchema;
  const {
    isContainerStep,
    isPlateLayoutStep,
    isEntityStep,
    isResiduesSupportedStep,
    isChemicalStructureSupportedStep,
    isPlateStep,
    isLocationStep,
    isBoxStep,
  } = getObjectTypesForLookupStepInfo(previousStepInfo);
  const stepTypes: Array<LookupStepType> = [];

  if (!isPlateLayoutStep && parentSchema) {
    const fieldStepTypes = [LookupStepType.SCHEMA_FIELD, LookupStepType.COUNT];
    if (parentSchema.fields.length !== 0) {
      stepTypes.push(...fieldStepTypes);
    } else if (initialDetailStep && fieldStepTypes.includes(initialDetailStep.lookupStep.type)) {
      // If there are no fields, there may still be an existing field step
      // if the fields have all been deleted
      stepTypes.push(initialDetailStep.lookupStep.type);
    }
  }
  const isParentStorableStep = isPlateStep || isBoxStep || isLocationStep;
  const isAnyStorableStep = isParentStorableStep || isContainerStep;
  // Available space is valid only for parent storables, not also containers
  if (isParentStorableStep) {
    stepTypes.push(LookupStepType.AVAILABLE_SPACE);
  }
  if (isAnyStorableStep) {
    stepTypes.push(LookupStepType.BARCODE, LookupStepType.LOCATION_PATH);
  }

  if (isContainerStep) {
    stepTypes.push(LookupStepType.WELL_COORDINATES, LookupStepType.QUANTITY, LookupStepType.CONCENTRATION);
    if (BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE) {
      stepTypes.push(LookupStepType.COMPUTED_EXPIRATION_DATE);
    }
    if (BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW) {
      stepTypes.push(LookupStepType.FREEZE_THAW_COUNT);
    }
    if (hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1)) {
      stepTypes.push(LookupStepType.SAMPLE_OWNERS, LookupStepType.AUTHORIZED_USERS);
    }
  } else if (isEntityStep) {
    stepTypes.push(LookupStepType.REGISTRY_IDENTIFIER);
    stepTypes.push(LookupStepType.ALIASES);
    stepTypes.push(LookupStepType.NAME);
    if (isResiduesSupportedStep) {
      stepTypes.push(LookupStepType.RESIDUES);
    }
    if (isChemicalStructureSupportedStep) {
      stepTypes.push(LookupStepType.CHEMICAL_STRUCTURE);
    }
  }
  return stepTypes.filter((stepType) => allowedLookupStepTypes.includes(stepType));
}

function getItems(
  previousStepInfo: LookupStepInfo,
  initialDetailStep: LookupStepInfo | null,
  detailStep: LookupStepInfo | null,
  allowedLookupStepTypes: Array<LookupStepType>,
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean,
  shouldHideLinkToPreviousStep?: boolean,
  injectedFinalLookupSteps?: Array<InjectedLookupStep>
): {items: Array<LookupDetailOption>; selectedItemIndex: number} {
  const lookupStepInfoWithType = getLookupStepInfoWithType(previousStepInfo);
  const parentSchema = lookupStepInfoWithType?.linkSchema;
  const stepTypes = getStepTypes(previousStepInfo, initialDetailStep, allowedLookupStepTypes);
  const items: Array<LookupDetailOption> = [];
  if (!shouldHideLinkToPreviousStep && allowedLookupStepTypes.includes(previousStepInfo.lookupStep.type)) {
    items.push({
      name: getStepDisplayInfo(previousStepInfo).name,
      icon: getIconForLookupStepInfo(previousStepInfo),
      lookupStep: null,
      parentSchema: parentSchema ?? null,
      linkSchema: previousStepInfo.linkSchema,
    });
  }

  if (injectedFinalLookupSteps?.length && parentSchema) {
    for (const {condition, name, icon, lookupStep, linkSchema} of injectedFinalLookupSteps) {
      // only inject the lookup step if the lookup step is of the correct type and its injection condition has
      // been met
      if (allowedLookupStepTypes.includes(lookupStep.type) && condition(parentSchema)) {
        items.push({
          name,
          icon,
          lookupStep,
          shouldSkipValidation: true,
          parentSchema,
          linkSchema,
        });
      }
    }
  }

  let selectedItemIndex = 0;
  for (const stepType of stepTypes) {
    if (stepType === LookupStepType.SCHEMA_FIELD) {
      if (!parentSchema) {
        continue;
      }
      if (initialDetailStep && initialDetailStep.lookupStep.type === LookupStepType.SCHEMA_FIELD) {
        // If the detail step was initially an archived field, ensure it remains in the list
        // and does not disappear when the user selects another option
        const {name, isArchived} = getStepDisplayInfo(initialDetailStep);
        if (isArchived) {
          items.push({
            ...initialDetailStep,
            name,
            icon: getIconForLookupStepInfo(initialDetailStep),
          });
        }
        if (isEqual(initialDetailStep.lookupStep, detailStep?.lookupStep)) {
          selectedItemIndex = items.length - 1;
        }
      }

      const schemaFields = getSchemaFieldsForLookupStep(parentSchema, false, schemaFieldFilterFn);
      for (const schemaField of schemaFields) {
        if (
          detailStep &&
          detailStep.lookupStep.type === LookupStepType.SCHEMA_FIELD &&
          detailStep.schemaField &&
          detailStep.schemaField.id === schemaField.id
        ) {
          const icon = getIconForLookupStepInfo(detailStep);
          items.push({
            ...detailStep,
            icon,
          });
          selectedItemIndex = items.length - 1;
        } else {
          const lookupStepInfo = getLookupStepInfoForFieldStepItem(stepType, schemaField, parentSchema);
          const icon = getIconForLookupStepInfo(lookupStepInfo);
          items.push({
            ...lookupStepInfo,
            icon,
          });
        }
      }
    } else if (detailStep && stepType === detailStep.lookupStep.type) {
      const icon = getIconForLookupStepInfo(detailStep);
      items.push({
        ...detailStep,
        icon,
      });
      selectedItemIndex = items.length - 1;
    } else if (stepType === LookupStepType.COUNT) {
      if (!parentSchema) {
        continue;
      }
      if (
        initialDetailStep &&
        !isEqual(initialDetailStep, detailStep) &&
        initialDetailStep.lookupStep.type === LookupStepType.COUNT
      ) {
        // If the detail step was initially an archived field, ensure it remains in the list
        // and does not disappear when the user selects another option
        if (getStepDisplayInfo(initialDetailStep).isArchived) {
          items.push({
            ...initialDetailStep,
            icon: getIconForLookupStepInfo(initialDetailStep),
          });
        }
      } else {
        const lookupStepInfo = getLookupStepInfoForCountStepItem(parentSchema, schemaFieldFilterFn, false);
        const icon = getIconForLookupStepInfo(lookupStepInfo);
        items.push({
          ...lookupStepInfo,
          icon,
          isDeleted: detailStep ? getStepDisplayInfo(detailStep).isDeleted : false,
        });
      }
    } else {
      const lookupStepInfo = getLookupStepInfoForNonFieldStepItem(stepType, parentSchema ?? null);
      const icon = getIconForLookupStepInfo(lookupStepInfo);
      items.push({
        ...lookupStepInfo,
        icon,
      });
    }
  }
  return {items, selectedItemIndex};
}

interface LookupDetailSelectorProps {
  previousStepInfo: LookupStepInfo;
  context?: LookupConfigModalContextInfo;
  initialDetailStep: LookupStepInfo | null;
  detailStep: LookupStepInfo | null;
  setDetailStep: (detailStep: LookupStepInfo | null) => void;
  shouldHideFilter?: boolean;
  allowedLookupStepTypes: Array<LookupStepType>;
  formErrors: FormErrors;
  useSchemaLanguage?: boolean;
  shouldHideLinkToPreviousStep?: boolean;
  injectedFinalLookupSteps?: Array<InjectedLookupStep>;
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean;
  allowAlternatingGridPatterns?: boolean;
}

function hasLookupStep(lookupStepInfo: Omit<LookupDetailOption, 'icon'>): lookupStepInfo is LookupStepInfo {
  return lookupStepInfo.lookupStep != null;
}

function LookupDetailSelector(props: LookupDetailSelectorProps): JSX.Element {
  const {items: initialItems, selectedItemIndex} = getItems(
    props.previousStepInfo,
    props.initialDetailStep,
    props.detailStep,
    props.allowedLookupStepTypes,
    props.schemaFieldFilterFn,
    props.shouldHideLinkToPreviousStep,
    props.injectedFinalLookupSteps
  );
  const [items, setItems] = useState(initialItems);
  const shouldHideLinkToPreviousStep = props.shouldHideLinkToPreviousStep;
  const setDetailStep = props.setDetailStep;
  const detailStep = props.detailStep;
  useEffect(() => {
    // If no detail step is currently selected verify that is appropriate behavior for the current
    // selected index by setting the detailStep to the currently selected item
    if (items.length && !detailStep) {
      const {icon, isDeleted, ...lookupStepInfo} = items[selectedItemIndex];
      if (hasLookupStep(lookupStepInfo)) {
        setDetailStep(lookupStepInfo);
      }
    }
  }, [shouldHideLinkToPreviousStep, setDetailStep, detailStep, items, selectedItemIndex]);

  return (
    <div
      className={css(styles.lookupDetailSelector)}
      {...getTestProps({component: 'LookupDetailSelector', element: 'root'})}
    >
      {items.map(({icon, isDeleted, ...lookupStepInfo}, i) => {
        const lookupStepInfoWithType = hasLookupStep(lookupStepInfo)
          ? getLookupStepInfoWithType(lookupStepInfo)
          : null;
        const lookupStepSchema = lookupStepInfoWithType?.linkSchema;
        return (
          <a
            key={i}
            onClick={() => {
              if (hasLookupStep(lookupStepInfo)) {
                props.setDetailStep(lookupStepInfo);
              } else {
                props.setDetailStep(null);
              }
            }}
            className={classNames(css(styles.lookupDetailSelectorItem), {
              [css(styles.lookupDetailSelectorItemSelected)]: selectedItemIndex === i,
            })}
          >
            {icon ? <BenchlingIcon icon={icon} className={css(styles.lookupDetailSelectorItemIcon)} /> : null}
            <span className="u-mini-spacing-right">{lookupStepInfo.name}</span>
            {hasLookupStep(lookupStepInfo) && isLookupStepConfigurable(lookupStepInfo.lookupStep) ? (
              <LookupStepForm
                disabled={selectedItemIndex !== i}
                isInline={true}
                lookupStepInfo={lookupStepInfo}
                previousStepInfo={props.previousStepInfo}
                context={props.context}
                allowAlternatingGridPatterns={props.allowAlternatingGridPatterns}
                schemaFieldFilterFn={props.schemaFieldFilterFn}
                handleSetLookupStep={(lookupStepInfo) => {
                  const newItems = [...items];
                  newItems[i] = {
                    ...items[i],
                    ...lookupStepInfo,
                  };
                  setItems(newItems);
                  props.setDetailStep(lookupStepInfo);
                }}
                formErrors={selectedItemIndex === i ? props.formErrors : {}}
                isFirstStep={false}
              />
            ) : null}
            {isDeleted ? <LookupConfigModalErrorIndicator error="Missing value" /> : null}
            {hasLookupStep(lookupStepInfo) && lookupStepSchema && !props.shouldHideFilter ? (
              <FilterLookupStepPopover
                className={css(styles.filterButton)}
                disabled={selectedItemIndex !== i}
                parentSchema={lookupStepSchema}
                filterLookupSteps={lookupStepInfo.filterLookupSteps || []}
                handleSetFilterLookupSteps={(filterLookupSteps: Array<FilterLookupStepInfo>) => {
                  const newItems = [...items];
                  const newLookupStepInfo = {
                    ...lookupStepInfo,
                    filterLookupSteps,
                  };
                  newItems[i] = {
                    ...items[i],
                    ...newLookupStepInfo,
                  };
                  setItems(newItems);
                  props.setDetailStep({
                    ...lookupStepInfo,
                    filterLookupSteps,
                  });
                }}
                schemaFieldFilterFn={props.schemaFieldFilterFn}
              />
            ) : null}
          </a>
        );
      })}
      {!items.length && (
        <div className={css(styles.lookupDetailSelectorItem, styles.placeholderLookupDetailSelectorItem)}>
          No available {props.useSchemaLanguage ? 'fields' : 'lookup attributes'}. Please select a new{' '}
          {props.useSchemaLanguage ? 'schema' : 'lookup object'} to continue.
        </div>
      )}
    </div>
  );
}

interface ReplicatesModalButtonProps {
  replicatesStep: (LookupStepInfo & {lookupStep: ReplicatesLookupStepSpec}) | null;
  setReplicatesStep: (
    replicatesStep: (LookupStepInfo & {lookupStep: ReplicatesLookupStepSpec}) | null
  ) => void;
  baseSchema: Schema | null;
}

function ReplicatesModalButton(props: ReplicatesModalButtonProps): JSX.Element {
  const handleOpenNumberLookupConfigModal = (): void => {
    runLookupConfigModalForLabAutomation({
      baseSchema: props.baseSchema,
      firstLookupStepTypes: FIRST_STEP_OPTIONS_FOR_COLUMNS_MAP.filter(
        (stepType) => stepType !== LookupStepType.CONSTANT
      ),
      intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
      finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
      initialLookupConfig: props.replicatesStep?.lookupStep.numberLookupConfig,
      onRequestClose: () => {},
      onRequestSubmit: ({lookupConfig}: {lookupConfig: LookupConfigSpec}) => {
        props.setReplicatesStep({
          name: 'Replicates',
          lookupStep: {type: LookupStepType.REPLICATES, numberLookupConfig: lookupConfig},
          parentSchema: props.baseSchema,
          linkSchema: null,
        });
      },
      getLookupConfigValidationError: (inferredType) => {
        if (LeafType.INTEGER !== inferredType.typeDef) {
          return 'Replicates must look up an integer value';
        }
        return null;
      },
      shouldShowIsMulti: true,
      title: 'Replicate by number lookup',
      allowReplicatesStep: false,
    });
  };

  const handleOpenReplicateByConstantModal = (): void => {
    runModal(ReplicateByConstantModal, {
      initialValue: props.replicatesStep
        ? getConstantReplicationFactor(props.replicatesStep.lookupStep)
        : null,
      onRequestClose: () => {},
      onRequestSubmit: (constantValue: number) => {
        const constantLookupStep: ConstantLookupStepSpec = {
          type: LookupStepType.CONSTANT,
          value: constantValue,
        };
        props.setReplicatesStep({
          name: 'Replicates',
          lookupStep: {
            type: LookupStepType.REPLICATES,
            numberLookupConfig: {
              isMulti: false,
              lookupSteps: [constantLookupStep],
            },
          },
          parentSchema: props.baseSchema,
          linkSchema: null,
        });
      },
    });
  };
  if (!props.replicatesStep) {
    return (
      <Dropdown
        menu={
          <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
            <Option onSelect={handleOpenReplicateByConstantModal}>By constant</Option>
            <Option onSelect={handleOpenNumberLookupConfigModal}>By number lookup</Option>
          </Menu>
        }
      >
        <div className={css(styles.setReplicates)}>Set replicates</div>
      </Dropdown>
    );
  }
  const handleOpenReplicateModal =
    getConstantReplicationFactor(props.replicatesStep.lookupStep) != null
      ? handleOpenReplicateByConstantModal
      : handleOpenNumberLookupConfigModal;
  return (
    <>
      <span className={css(styles.setReplicates)} onClick={handleOpenReplicateModal}>
        Edit replicates
      </span>
      <span
        className={css(styles.removeReplicates)}
        onClick={() => {
          props.setReplicatesStep(null);
        }}
      >
        Remove replicates
      </span>
    </>
  );
}

export interface LookupConfigDetailPaneCheckboxesProps {
  shouldShowIsMulti?: boolean;
  shouldShowIsMultiFlatten?: boolean;
  shouldShowFinalSortOption: boolean;
  isMulti: boolean;
  setIsMulti: (val: boolean) => void;
  multiFlattenLevel?: number | null;
  setMultiFlattenLevel: (val: number | null) => void;
  finalSortType?: LookupSortType;
  setFinalSortType: (sortType: LookupSortType) => void;
}

function LookupConfigDetailPaneCheckboxes(props: LookupConfigDetailPaneCheckboxesProps): JSX.Element | null {
  const {
    shouldShowIsMulti,
    shouldShowIsMultiFlatten,
    shouldShowFinalSortOption,
    isMulti,
    setIsMulti,
    multiFlattenLevel,
    setMultiFlattenLevel,
    finalSortType,
    setFinalSortType,
  } = props;
  if (shouldShowIsMulti || shouldShowIsMultiFlatten || shouldShowFinalSortOption) {
    return (
      <div className={css(styles.checkboxesWrapper)}>
        {shouldShowIsMulti ? (
          <CheckboxInput
            id="is-multi"
            className={css(styles.isMulti)}
            checked={!isMulti}
            label="Consolidate results into single row"
            onChange={(e) => {
              setIsMulti(!e.target.checked);
            }}
          />
        ) : null}
        {shouldShowIsMultiFlatten ? (
          <CheckboxInput
            id="is-multi-flatten"
            disabled={!isMulti}
            checked={multiFlattenLevel == null}
            label="Do not preserve relationship between lookup steps"
            onChange={(e) => {
              setMultiFlattenLevel(e.target.checked ? null : 1);
            }}
          />
        ) : null}
        {shouldShowFinalSortOption && (
          // There are only two options for sort type, so we just use a checkbox for now
          <CheckboxInput
            id="is-alphabetical"
            // default to true
            checked={finalSortType === LookupSortType.ALPHABETICAL || !finalSortType}
            label="Sort multiple values alphabetically"
            onChange={(e) => {
              setFinalSortType(e.target.checked ? LookupSortType.ALPHABETICAL : LookupSortType.ORIGINAL);
            }}
          />
        )}
      </div>
    );
  }
  return null;
}

export interface LookupConfigDetailPaneProps {
  modalProps?: Partial<ModalProps>;
  context?: LookupConfigModalContextInfo;
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean;
  previousLookupStepInfos: Array<LookupStepInfo>;
  title: string;
  baseSchema: Schema | null;
  shouldHideFilter?: boolean;
  finalLookupStepTypes: Array<LookupStepType>;
  isMulti: boolean;
  setIsMulti: (val: boolean) => void;
  multiFlattenLevel?: number | null;
  setMultiFlattenLevel: (val: number | null) => void;
  initialDetailStep: LookupStepInfo | null;
  detailStep: LookupStepInfo | null;
  setDetailStep: (detailStep: LookupStepInfo | null) => void;
  allowReplicatesStep?: boolean;
  replicatesStep: (LookupStepInfo & {lookupStep: ReplicatesLookupStepSpec}) | null;
  setReplicatesStep: (detailStep: LookupStepInfo & {lookupStep: ReplicatesLookupStepSpec}) => void;
  processTasksStep: (LookupStepInfo & {lookupStep: TaskFinderProcessTasksLookupStepSpec}) | null;
  setProcessTasksStep: (
    processTasksStep: LookupStepInfo & {lookupStep: TaskFinderProcessTasksLookupStepSpec}
  ) => void;
  onGoToPrevPane: () => void;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  useSchemaLanguage?: boolean;
  description?: ReactNode;
  shouldHideLinkToPreviousStepInDetailSelector?: boolean;
  submitButtonText?: string;
  injectedFinalLookupSteps?: Array<InjectedLookupStep>;
  submitButtonDataTestAttrs?: DataTestAttrs;
  shouldShowIsMulti?: boolean;
  shouldShowIsMultiFlatten?: boolean;
  shouldShowFinalSortOption: boolean;
  lookupConfigValidationError?: string | null;
  allowAlternatingGridPatterns?: boolean;
  finalSortType: LookupSortType | undefined;
  setFinalSortType: (sortType: LookupSortType) => void;
}

export default function LookupConfigDetailPane(props: LookupConfigDetailPaneProps): JSX.Element {
  const [formErrors, setFormErrors] = useState(
    validateLookupSteps(compact([props.detailStep]), props.baseSchema)
  );
  const errorBannerDetails = useMemo(() => {
    if (props.lookupConfigValidationError != null) {
      return {bannerType: InlineBannerType.ERROR, text: props.lookupConfigValidationError};
    }
    return null;
  }, [props.lookupConfigValidationError]);

  return (
    <Modal
      {...props.modalProps}
      buttons={[
        {
          key: 'prev',
          text: 'Back',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: props.onGoToPrevPane,
        },
        {
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: props.shouldHideLinkToPreviousStepInDetailSelector && !props.detailStep,
          text: props.submitButtonText || (props.useSchemaLanguage ? 'Done' : 'Insert'),
          dataTestAttrs: props.submitButtonDataTestAttrs,
        },
      ]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        const newFormErrors = validateLookupSteps(compact([props.detailStep]), props.baseSchema);
        setFormErrors(newFormErrors);
        if (isFormErrorsEmpty(newFormErrors)) {
          props.onRequestSubmit();
        }
      }}
      title={props.title}
      footer={
        <LookupConfigDetailPaneCheckboxes
          shouldShowIsMulti={props.shouldShowIsMulti}
          shouldShowIsMultiFlatten={props.shouldShowIsMultiFlatten}
          shouldShowFinalSortOption={props.shouldShowFinalSortOption}
          isMulti={props.isMulti}
          setIsMulti={props.setIsMulti}
          multiFlattenLevel={props.multiFlattenLevel}
          setMultiFlattenLevel={props.setMultiFlattenLevel}
          finalSortType={props.finalSortType}
          setFinalSortType={props.setFinalSortType}
        />
      }
      bannerProps={errorBannerDetails}
      scrollable={true}
    >
      {props.description && <div className="u-moderate-spacing-bottom">{props.description}</div>}
      <div className="u-moderate-spacing-bottom">
        <LookupConfigModalPaneIndicator paneIndex={1} useSchemaLanguage={props.useSchemaLanguage} />
      </div>
      <div className="u-moderate-spacing-bottom">
        <div className={css(styles.sectionTitle)}>{props.useSchemaLanguage ? 'Schema' : 'Lookup object'}</div>
        <LookupInfosDescription lookupStepInfos={props.previousLookupStepInfos} />
      </div>
      <div>
        <div className={css(styles.sectionTitle)}>
          {props.useSchemaLanguage ? 'Field' : 'Lookup attribute'}
        </div>
        <LookupDetailSelector
          previousStepInfo={props.previousLookupStepInfos[props.previousLookupStepInfos.length - 1]}
          context={props.context}
          initialDetailStep={props.initialDetailStep}
          detailStep={props.detailStep}
          setDetailStep={props.setDetailStep}
          shouldHideFilter={props.shouldHideFilter}
          allowedLookupStepTypes={props.finalLookupStepTypes}
          formErrors={formErrors}
          useSchemaLanguage={props.useSchemaLanguage}
          shouldHideLinkToPreviousStep={props.shouldHideLinkToPreviousStepInDetailSelector}
          injectedFinalLookupSteps={props.injectedFinalLookupSteps}
          schemaFieldFilterFn={props.schemaFieldFilterFn}
          allowAlternatingGridPatterns={props.allowAlternatingGridPatterns}
        />
      </div>
      {props.allowReplicatesStep ? (
        <div className="u-small-spacing-top">
          <ReplicatesModalButton
            replicatesStep={props.replicatesStep}
            setReplicatesStep={props.setReplicatesStep}
            baseSchema={props.baseSchema ?? null}
          />
        </div>
      ) : null}
      {props.context?.processTaskSchema != null && (
        <div className="u-moderate-spacing-top">
          <ProcessTasksLookupStepDropdown
            processTaskSchema={props.context?.processTaskSchema}
            processTasksStep={props.processTasksStep!}
            onChange={props.setProcessTasksStep}
          />
        </div>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
    ...miniSpacingBottom,
  },
  lookupDetailSelector: {
    borderRadius: '4px',
    border: `1px solid ${GRAY}`,
    fontFamily: FONT_FAMILY_PROXIMA,
    overflowY: 'scroll',
    maxHeight: '300px',
  },
  lookupDetailSelectorItem: {
    color: GRAY_DARKER,
    ':hover': {
      backgroundColor: BLUE_POWDER_LIGHT,
      textDecoration: 'none',
    },
    display: 'flex',
    fontSize: '13px',
    fontWeight: 400,
    padding: '10px',
    alignItems: 'center',
  },
  placeholderLookupDetailSelectorItem: {
    ':hover': {
      backgroundColor: 'initial',
    },
  },
  lookupDetailSelectorItemIcon: {
    marginRight: '4px',
  },
  lookupDetailSelectorItemSelected: {
    backgroundColor: BLUE_POWDER_LIGHT,
    color: BLUE_LIGHT,
  },
  lookupStepChip: {
    marginRight: '0px',
    verticalAlign: 'top',
  },
  setReplicates: {
    color: BLUE_LIGHT,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '13px',
  },
  removeReplicates: {
    color: RED_LIGHT,
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: '24px',
  },
  checkboxesWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  isMulti: {
    marginTop: '-2px',
    marginBottom: '6px',
  },
  filterButton: {
    marginLeft: 'auto',
  },
});
